import { GridColDef } from "@mui/x-data-grid-pro";
import { IconButton } from "@mui/material";

import ItemStatus from "../../../../../../common/components/item_status";
import dayjs from "dayjs";
import DynamicIcon from "../../../../../../common/components/dynamic_icon";

/**
 * GenerateAssistenciaTecnicaListaColumnDefinition
 */

type Props = {
  onActionEditar: (idAssistenciaTecnica: number, openNewTab: boolean) => void;
  onActionDelete: (idAssistenciaTecnica: number, openModal: boolean) => void;
};

export const GenerateAssistenciaTecnicaListaColumnDefinition = ({
  onActionEditar,
  onActionDelete,
}: Props): GridColDef[] => {

  const computeStatusTitle = (status: number) => {
    switch (status) {
      case 1:
        return "E";
      case 0:
        return "A";
      default:
        return "?";
    }
  };

  const computeStatusColor = (status: number) => {
    switch (status) {
      case 1:
        return "red";
      case 0:
        return "yellow";
      default:
        return "blue";
    }
  };

  const computeStatusDescription = (status: number) => {
    switch (status) {
      case 1:
        return "Encerrada";
      case 0:
        return "Aberta";
      default:
        return "?";
    }
  };

  const computeItemStatusTitle = (status: number) => {
    switch (status) {
      case 3:
        return "Paralisada";
      case 2:
        return "Alta";
      case 1:
        return "Média";
      case 0:
        return "Baixa";
      default:
        return "?";
    }
  };

  const computeItemStatusColor = (status: number) => {
    switch (status) {
      case 3:
        return "light-blue";
      case 2:
        return "red";
      case 1:
        return "yellow";
      case 0:
        return "gray";
      default:
        return "blue";
    }
  };

  const computeItemStatusDescription = (status: number) => {
    switch (status) {
      case 3:
        return "Paralisada";
      case 2:
        return "Alta";
      case 1:
        return "Média";
      case 0:
        return "Baixa";
      default:
        return "?";
    }
  };

  const computeItemTipoTitle = (tipo: number) => {
    switch (tipo) {
      case 1:
        return "E";
      case 0:
        return "I";
      default:
        return "?";
    }
  };

  const computeItemTipoColor = (tipo: number) => {
    switch (tipo) {
      case 1:
        return "green";
      case 0:
        return "light-blue";
      default:
        return "blue";
    }
  };

  const computeItemTipoDescription = (tipo: number) => {
    switch (tipo) {
      case 1:
        return "Externa";
      case 0:
        return "Interna";
      default:
        return "?";
    }
  };

  const computeItemProcedenteTitle = (tipo: number) => {
    switch (tipo) {
      case 2:
        return "Não";
      case 1:
        return "Sim";
      case 0:
        return "AV";
      default:
        return "?";
    }
  };

  const computeItemProcedenteColor = (tipo: number) => {
    switch (tipo) {
      case 2:
        return "red";
      case 1:
        return "green";
      case 0:
        return "gray";
      default:
        return "blue";
    }
  };

  const computeItemProcedenteDescription = (tipo: number) => {
    switch (tipo) {
      case 2:
        return "Não";
      case 1:
        return "Sim";
      case 0:
        return "Aguardando Vistoria";
      default:
        return "?";
    }
  };

  const computeItemResolvidoTitle = (tipo: number) => {
    switch (tipo) {
      case 3:
        return "RI";
      case 2:
        return "Não";
      case 1:
        return "Sim";
      case 0:
        return "AR";
      default:
        return "?";
    }
  };

  const computeItemResolvidoColor = (tipo: number) => {
    switch (tipo) {
      case 3:
        return "yellow";
      case 2:
        return "red";
      case 1:
        return "green";
      case 0:
        return "gray";
      default:
        return "blue";
    }
  };

  const computeItemResolvidoDescription = (tipo: number) => {
    switch (tipo) {
      case 3:
        return "Reparo Iniciado";
      case 2:
        return "Não";
      case 1:
        return "Sim";
      case 0:
        return "Aguardando Reparo";
      default:
        return "?";
    }
  };

  return [
    {
      field: "statusAssistencia",
      filterable: false,
      headerName: "Status",
      minWidth: 55,
      width: 55,
      sortable: false,
      renderCell: ({ value }) => (
        <div className="w-full h-full flex justify-center items-center">
          <ItemStatus
            size="autowidth"
            textSize="small"
            title={computeStatusTitle(value)}
            color={computeStatusColor(value)}
            description={computeStatusDescription(value)}
          />
        </div>
      )
    },
    {
      field: "prioridade",
      filterable: false,
      headerName: "Prioridade",
      minWidth: 70,
      width: 70,
      sortable: false,
      renderCell: ({ value }) => (
        <div className="w-full h-full flex justify-center items-center">
          <ItemStatus
            size="autowidth"
            textSize="small"
            title={computeItemStatusTitle(value)}
            color={computeItemStatusColor(value)}
            description={computeItemStatusDescription(value)}
          />
        </div>
      )
    },
    {
      field: "tipoAssistencia",
      filterable: false,
      headerName: "Tipo",
      minWidth: 45,
      width: 45,
      sortable: false,
      renderCell: ({ value }) => (
        <div className="w-full h-full flex justify-center items-center">
          <ItemStatus
            size="autowidth"
            textSize="small"
            title={computeItemTipoTitle(value)}
            color={computeItemTipoColor(value)}
            description={computeItemTipoDescription(value)}
          />
        </div>
      )
    },
    {
      field: "codigo",
      filterable: false,
      headerName: "Código Sistema",
      width: 60,
      minWidth: 60,
      sortable: false,
      align: 'center',
    },
    {
      field: "nroChamado",
      filterable: false,
      headerName: "Nº do Chamado",
      width: 70,
      minWidth: 70,
      sortable: false,
      align: 'left',
    },
    {
      field: "cliente",
      filterable: false,
      headerName: "Cliente",
      flex: 1,
      minWidth: 130,
      sortable: false,
      align: 'left',
      valueGetter: (_, row) =>
        row['clienteCadastrado'] === true
          ? row['nomeFantasia']
          : row['descCliente'],
      renderCell: ({ value }) => <span>{value}</span>,
    },
    {
      field: "centroCusto",
      filterable: false,
      headerName: "Centro de Custo",
      flex: 1,
      minWidth: 150,
      sortable: false,
      align: 'left',
    },
    {
      field: "local",
      filterable: false,
      headerName: "Unidade",
      width: 130,
      minWidth: 130,
      sortable: false,
      align: 'left',
    },
    {
      field: "data",
      filterable: false,
      headerName: "Data Abertura",
      width: 80,
      minWidth: 80,
      sortable: false,
      align: 'left',
      renderCell: ({ value }) => <span>{value ? dayjs.utc(value).local().format("DD/MM/YYYY") : ""}</span>
    },
    {
      field: "precedente",
      filterable: false,
      headerName: "Procedente",
      minWidth: 70,
      width: 70,
      sortable: false,
      renderCell: ({ value }) => (
        <div className="w-full h-full flex justify-center items-center">
          <ItemStatus
            size="autowidth"
            textSize="small"
            title={computeItemProcedenteTitle(value)}
            color={computeItemProcedenteColor(value)}
            description={computeItemProcedenteDescription(value)}
          />
        </div>
      )
    },
    {
      field: "nomeCategoria",
      filterable: false,
      headerName: "Categorias",
      width: 100,
      minWidth: 100,
      sortable: false,
      align: 'left',
    },
    {
      field: "resolvido",
      filterable: false,
      headerName: "Resolvido",
      minWidth: 70,
      width: 70,
      sortable: false,
      renderCell: ({ value }) => (
        <div className="w-full h-full flex justify-center items-center">
          <ItemStatus
            size="autowidth"
            textSize="small"
            title={computeItemResolvidoTitle(value)}
            color={computeItemResolvidoColor(value)}
            description={computeItemResolvidoDescription(value)}
          />
        </div>
      )
    },
    {
      field: "dataInicio",
      filterable: false,
      headerName: "Previsão Vistoria",
      width: 80,
      minWidth: 80,
      sortable: false,
      align: 'left',
      renderCell: ({ value }) => <span>{value ? dayjs.utc(value).local().format("DD/MM/YYYY") : ""}</span>
    },
    {
      field: "dataTermino",
      filterable: false,
      headerName: "Prev. Térm. Assistência",
      width: 80,
      minWidth: 80,
      sortable: false,
      align: 'left',
      renderCell: ({ value }) => <span>{value ? dayjs.utc(value).local().format("DD/MM/YYYY") : ""}</span>
    },
    {
      field: "dataEncerramento",
      filterable: false,
      headerName: "Data de Encerramento",
      width: 90,
      minWidth: 90,
      sortable: false,
      align: 'left',
      renderCell: ({ value }) => <span>{value ? dayjs.utc(value).local().format("DD/MM/YYYY") : ""}</span>
    },
    {
      field: "tempoProcesso",
      filterable: false,
      headerName: "Tempo Processo (dias)",
      width: 70,
      minWidth: 70,
      sortable: false,
      align: 'center',
      valueGetter: (_, row) =>
        row['data'] && row['dataEncerramento']
          ? dayjs.utc(row['dataEncerramento']).diff(dayjs.utc(row['data']), 'day')
          : undefined,
    },
    {
      field: "percPonto",
      filterable: false,
      headerName: "Índice de Satisfação",
      width: 80,
      minWidth: 80,
      sortable: false,
      align: 'center',
      renderCell: ({ value }) => (
        <span>
          {
            new Intl.NumberFormat('pt-BR', {
              style: 'percent',
              minimumFractionDigits: 2
            }).format(value)
          }
        </span>
      )
    },
    {
      field: 'idAssistenciaTecnica',
      sortable: false,
      filterable: false,
      headerName: 'Ações',
      width: 100,
      resizable: false,
      renderCell: ({ value }) => {
        return (
          <div className="flex items-center justify-center w-full h-full gap-2">
            <IconButton
              onMouseDown={e => {
                if (e.button === 1) {
                  onActionEditar(value, true);
                } else {
                  onActionEditar(value, e.ctrlKey);
                }
                e.preventDefault();
              }}>
              <DynamicIcon nameIcon="EditRounded" size={25} className="dark:text-white text-primary-green" />
            </IconButton>

            <IconButton onClick={() => { onActionDelete(value, true); }}>
              <DynamicIcon nameIcon="DeleteForeverRounded" size={25} className="text-red-500" />
            </IconButton>
          </div>
        );
      }
    },
  ];
}